import { API_CONFIG } from '../config/api';
import { trackEvent } from '../utils/analytics';
import { apiFetch, buildResponseError, parseResponseBody } from '../utils/http';

// Create a simple API utility for AI service
const createApiCall = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    data = null,
    headers = {},
    requireAuth = true
  } = options;

  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const response = await apiFetch(url, {
    method,
    headers,
    data,
    credentials: requireAuth ? 'include' : 'include'
  });
  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    throw buildResponseError(response, responseBody);
  }

  return responseBody ?? {};
};

// AI Service for managing AI-related API calls
class AIService {
  constructor() {
    // Removed: this.logInfo('AIService initialized');
  }

  // Logging utility
  logInfo(message, data = null) {
    // Removed for production - only keep for development debugging if needed
    // console.log(`[AIService] ${message}`, data || '');
  }

  logError(message, error = null) {
    console.error(`[AIService] ERROR: ${message}`, error || '');
  }

  /**
   * Generate images using AI
   * @param {Object} imageOptions - Image generation options
   * @returns {Promise<Object>} Generated images response
   */
  async generateImages(imageOptions = {}) {
    // Removed: this.logInfo('Generating images with AI', imageOptions);

    try {
      const defaultOptions = {
        count: 1,
        size: '1024x1024',
        quality: 'standard'
      };
      
      const options = { ...defaultOptions, ...imageOptions };
      
      // Validate required prompt
      if (!options.prompt || options.prompt.trim().length === 0) {
        throw new Error('Prompt is required for image generation');
      }

      const response = await createApiCall(API_CONFIG.ENDPOINTS.AI.GENERATE_IMAGES, {
        method: 'POST',
        data: options
      });

      // Removed: this.logInfo('Images generated successfully', response);
      trackEvent('ai_generate', {
        ai_task: 'images',
        image_count: options.count,
        image_size: options.size,
        image_quality: options.quality
      });
      return response;
    } catch (error) {
      this.logError('Failed to generate images', error);
      throw error;
    }
  }

  /**
   * Generate or regenerate strategy for campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} strategyOptions - Strategy generation options
   * @returns {Promise<Object>} Generated strategy response
   */
  async generateStrategy(campaignId, strategyOptions = {}) {
    // Removed: this.logInfo('Generating/regenerating strategy for campaign', { campaignId, strategyOptions });

    try {
      const options = {
        include_company_analysis: strategyOptions.include_company_analysis ?? true,
        ...(strategyOptions.user_prompt && { user_prompt: strategyOptions.user_prompt })
      };
      
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AI.STRATEGY(campaignId), {
        method: 'POST',
        data: options
      });
      // Removed: this.logInfo('Strategy generated/regenerated successfully', response);
      trackEvent('ai_generate', {
        ai_task: 'strategy',
        include_company_analysis: options.include_company_analysis
      });
      return response;
    } catch (error) {
      this.logError('Failed to generate/regenerate strategy', error);
      throw error;
    }
  }

  /**
   * Generate posts for campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} postOptions - Post generation options
   * @returns {Promise<Object>} Generated posts response
   */
  async generatePosts(campaignId, postOptions = {}) {
    // Removed: this.logInfo('Generating posts for campaign', { campaignId, postOptions });

    try {
      const defaultOptions = {
        count: 5,
        content_type: 'promotional'
      };
      
      const options = { ...defaultOptions, ...postOptions };
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AI.POSTS(campaignId), {
        method: 'POST',
        data: options
      });
      // Removed: this.logInfo('Posts generated successfully', response);
      trackEvent('ai_generate', {
        ai_task: 'posts',
        post_count: options.count,
        content_type: options.content_type
      });
      return response;
    } catch (error) {
      this.logError('Failed to generate posts', error);
      throw error;
    }
  }

  /**
   * Check AI services health
   * @returns {Promise<Object>} Health check response
   */
  async checkHealth() {
    // Removed: this.logInfo('Checking AI services health');

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AI.HEALTH, {
        requireAuth: false
      });
      // Removed: this.logInfo('AI services health check successful', response);
      return response;
    } catch (error) {
      this.logError('AI services health check failed', error);
      throw error;
    }
  }
}

// Create singleton instance
const aiService = new AIService();

export default aiService;
